import { memoriesOf } from 'helpers/memories';
import { AlbumsFilters, AlbumsSortBy, AlbumContentType } from 'common/enums';
import { Album } from 'models/album';
import { vaultDetailActions } from './action-types';

export type AlbumCustomFilters = Record<AlbumContentType, boolean> | null;

// Dashboard scope tabs: "My Stuff" (this vault's albums) vs "Shared With Me"
// (albums other people shared with the current user).
export type DashboardScope = 'all' | 'shared';

export type ActiveAlbumPointer = {
  id: number;
  listType: 'answered' | 'unanswered';
} | null;

export type InitialStateType = {
  vaultId: number;
  albumFilter: AlbumsFilters;
  albumCustomFilters: AlbumCustomFilters | null;
  albumsSortBy: AlbumsSortBy | null;
  searchQuery: string;
  scope: DashboardScope;
  answeredAlbums: Album[];
  unansweredAlbums: Album[];
  activeAlbumPointer: ActiveAlbumPointer;
  // Bumped by anything that creates content outside the album screen (the
  // top-bar "New" modal). The vault page re-fetches when it changes, so a new
  // memory shows up without a manual reload.
  refreshToken: number;
};

const initialState: InitialStateType = {
  vaultId: -1,
  albumFilter: AlbumsFilters.all,
  albumCustomFilters: null,
  albumsSortBy: null,
  searchQuery: '',
  scope: 'all',
  answeredAlbums: [],
  unansweredAlbums: [],
  activeAlbumPointer: null,
  refreshToken: 0,
};

type Action = {
  type: string;
  vaultId?: number;
  albumFilter?: AlbumsFilters;
  albumCustomFilters?: AlbumCustomFilters;
  albumsSortBy?: AlbumsSortBy;
  searchQuery?: string;
  scope?: DashboardScope;
  albums?: Album[];
  album?: Album;
  albumId?: number;
  updates?: Partial<Album>;
  activeAlbumPointer?: ActiveAlbumPointer;
};

const VaultDetailReducer = (state: InitialStateType, action: Action) => {
  switch (action.type) {
    case vaultDetailActions.UPDATE_VAULT_ID:
      return { ...state, vaultId: action.vaultId! };
    case vaultDetailActions.REQUEST_ALBUMS_REFRESH:
      return { ...state, refreshToken: state.refreshToken + 1 };
    case vaultDetailActions.UPDATE_ALBUM_FILTER:
      return { ...state, albumFilter: action.albumFilter! };
    case vaultDetailActions.UPDATE_ALBUM_CUSTOM_FILTERS:
      return { ...state, albumCustomFilters: action.albumCustomFilters! };
    case vaultDetailActions.UPDATE_ALBUMS_SORT_BY:
      return { ...state, albumsSortBy: action.albumsSortBy! };
    case vaultDetailActions.SET_SEARCH_QUERY:
      return { ...state, searchQuery: action.searchQuery ?? '' };
    case vaultDetailActions.SET_DASHBOARD_SCOPE:
      return { ...state, scope: action.scope ?? 'all' };

    case vaultDetailActions.SET_INITIAL_ALBUMS: {
      const allAlbums = action.albums || [];
      const answered = allAlbums.filter(
        (album) => memoriesOf(album).length > 0,
      );
      const unanswered = allAlbums.filter(
        (album) => memoriesOf(album).length === 0,
      );
      return {
        ...state,
        answeredAlbums: answered,
        unansweredAlbums: unanswered,
      };
    }

    case vaultDetailActions.ADD_TO_UNANSWERED: {
      const maxGlobalIndex = Math.max(
        ...state.answeredAlbums.map((m) => m.globalOrderIndex),
        ...state.unansweredAlbums.map((m) => m.globalOrderIndex),
        0,
      );
      const albumWithIndex = {
        ...action.album!,
        globalOrderIndex:
          action.album!.globalOrderIndex !== undefined
            ? action.album!.globalOrderIndex
            : maxGlobalIndex + 1,
      };
      return {
        ...state,
        unansweredAlbums: [...state.unansweredAlbums, albumWithIndex],
      };
    }

    case vaultDetailActions.MOVE_TO_ANSWERED: {
      const albumToMove = state.unansweredAlbums.find(
        (m) => m.id === action.albumId,
      );
      if (!albumToMove) return state;
      return {
        ...state,
        unansweredAlbums: state.unansweredAlbums.filter(
          (m) => m.id !== action.albumId,
        ),
        answeredAlbums: [...state.answeredAlbums, albumToMove],
      };
    }

    case vaultDetailActions.MOVE_TO_UNANSWERED: {
      const albumToMove = state.answeredAlbums.find(
        (m) => m.id === action.albumId,
      );
      if (!albumToMove) return state;
      return {
        ...state,
        answeredAlbums: state.answeredAlbums.filter(
          (m) => m.id !== action.albumId,
        ),
        unansweredAlbums: [...state.unansweredAlbums, albumToMove],
      };
    }

    case vaultDetailActions.DELETE_ANSWERED:
      return {
        ...state,
        answeredAlbums: state.answeredAlbums.filter(
          (m) => m.id !== action.albumId,
        ),
      };

    case vaultDetailActions.DELETE_UNANSWERED:
      return {
        ...state,
        unansweredAlbums: state.unansweredAlbums.filter(
          (m) => m.id !== action.albumId,
        ),
      };

    case vaultDetailActions.UPDATE_ANSWERED_ALBUM:
      return {
        ...state,
        answeredAlbums: state.answeredAlbums.map((m) =>
          m.id === action.albumId ? { ...m, ...action.updates } : m,
        ),
      };

    case vaultDetailActions.UPDATE_UNANSWERED_ALBUM:
      return {
        ...state,
        unansweredAlbums: state.unansweredAlbums.map((m) =>
          m.id === action.albumId ? { ...m, ...action.updates } : m,
        ),
      };

    case vaultDetailActions.SET_ACTIVE_ALBUM:
      return {
        ...state,
        activeAlbumPointer: action.activeAlbumPointer ?? null,
      };

    case vaultDetailActions.CLEAR_ACTIVE_ALBUM:
      return {
        ...state,
        activeAlbumPointer: null,
      };

    case vaultDetailActions.REORDER_ANSWERED_ALBUMS:
      return {
        ...state,
        answeredAlbums: action.albums!,
      };

    case vaultDetailActions.REORDER_UNANSWERED_ALBUMS:
      return {
        ...state,
        unansweredAlbums: action.albums!,
      };

    default:
      return state;
  }
};

export { initialState, VaultDetailReducer };
